import type { AnalisePorCriterio } from "../../services";


export function analiseCriterioComRespostaAposSolicitacao(
  a: AnalisePorCriterio | null | undefined,
): boolean {
  if (!a?.adequadoEm) return false;
  if (!a.solicitadoEm) return true;
  const tAde = new Date(a.adequadoEm).getTime();
  const tSol = new Date(a.solicitadoEm).getTime();
  return Number.isFinite(tAde) && Number.isFinite(tSol) && tAde >= tSol;
}

function tsSolicitado(a: AnalisePorCriterio): number {
  const s = a?.solicitadoEm;
  if (!s || String(s).trim() === "") return Number.NEGATIVE_INFINITY;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

function tsAdequado(a: AnalisePorCriterio): number {
  const s = a?.adequadoEm;
  if (!s || String(s).trim() === "") return Number.NEGATIVE_INFINITY;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

function idAnalise(a: AnalisePorCriterio): number {
  const id = Number(a?.id ?? Number.NEGATIVE_INFINITY);
  return Number.isFinite(id) ? id : Number.NEGATIVE_INFINITY;
}


function cicloAdequacaoEncerrado(a: AnalisePorCriterio): boolean {
  return (
    Boolean(a?.solicitadoEm && String(a.solicitadoEm).trim() !== "") &&
    analiseCriterioComRespostaAposSolicitacao(a)
  );
}


export function listarAnalisesCriterio(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
): AnalisePorCriterio[] {
  if (analise == null) return [];
  if (Array.isArray(analise)) return analise.filter(Boolean) as AnalisePorCriterio[];
  return [analise];
}


export function listarCiclosSolicitacaoAdequacaoOrdenados(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
): AnalisePorCriterio[] {
  const arr = listarAnalisesCriterio(analise).filter(
    (a) => a?.solicitadoEm != null && String(a.solicitadoEm).trim() !== "",
  );
  arr.sort((a, b) => tsSolicitado(a) - tsSolicitado(b));
  return arr;
}


export function pickAnaliseAtualPorCriterio(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
): AnalisePorCriterio | null {
  const arr = listarAnalisesCriterio(analise);
  if (arr.length === 0) return null;

  const pending = arr.filter(
    (a) =>
      Boolean(a?.solicitadoEm && String(a.solicitadoEm).trim() !== "") &&
      !analiseCriterioComRespostaAposSolicitacao(a),
  );
  const pool = pending.length > 0 ? pending : arr;

  return pool.reduce((best, cur) =>
    tsSolicitado(cur) >= tsSolicitado(best) ? cur : best,
  );
}


export function pickAnaliseLinhaEdicaoParecer(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
): AnalisePorCriterio | null {
  const arr = listarAnalisesCriterio(analise);
  if (arr.length === 0) return null;
  if (arr.length === 1) return arr[0]!;

  const pending = arr.filter(
    (a) =>
      Boolean(a?.solicitadoEm && String(a.solicitadoEm).trim() !== "") &&
      !analiseCriterioComRespostaAposSolicitacao(a),
  );
  if (pending.length > 0) {
    return pending.reduce((best, cur) =>
      tsSolicitado(cur) >= tsSolicitado(best) ? cur : best,
    );
  }

  const naoEncerrados = arr.filter((a) => !cicloAdequacaoEncerrado(a));
  const pool = naoEncerrados.length > 0 ? naoEncerrados : arr;

  return pool.reduce((best, cur) => {
    const idDiff = idAnalise(cur) - idAnalise(best);
    if (idDiff !== 0) return idDiff > 0 ? cur : best;
    return tsSolicitado(cur) >= tsSolicitado(best) ? cur : best;
  });
}


export function pickObservacaoAnalistaUltimoCicloEncerrado(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
): string | null {
  const arr = listarAnalisesCriterio(analise);
  const done = arr.filter((a) => analiseCriterioComRespostaAposSolicitacao(a));
  done.sort((a, b) => tsAdequado(b) - tsAdequado(a));
  for (const x of done) {
    const o = x.observacaoAnalista;
    if (o != null && String(o).trim() !== "") return String(o);
  }
  return null;
}


export function pickObservacaoAnalistaMaisRecente(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
  analiseReferencia?: AnalisePorCriterio | null,
): string | null {
  const arr = listarAnalisesCriterio(analise);
  const refTs = analiseReferencia
    ? tsSolicitado(analiseReferencia)
    : Number.NEGATIVE_INFINITY;
  const refId = analiseReferencia
    ? idAnalise(analiseReferencia)
    : Number.NEGATIVE_INFINITY;
  const scoped = analiseReferencia
    ? arr.filter((x) => {
        const t = tsSolicitado(x);
        const id = idAnalise(x);
        const sameOrNewerByDate = t >= refTs;
        const sameOrNewerById = id >= refId;
        return sameOrNewerByDate || sameOrNewerById;
      })
    : arr;
  const pool = scoped.length > 0 ? scoped : arr;
  const ordered = [...pool].sort((a, b) => {
    const tsDiff = tsSolicitado(b) - tsSolicitado(a);
    if (tsDiff !== 0) return tsDiff;
    const idDiff = idAnalise(b) - idAnalise(a);
    if (idDiff !== 0) return idDiff;
    return tsAdequado(b) - tsAdequado(a);
  });
  for (const x of ordered) {
    const o = x.observacaoAnalista;
    if (o != null && String(o).trim() !== "") return String(o);
  }
  return null;
}


export function pickObservacaoSindicatoUltimoCicloEncerrado(
  analise: AnalisePorCriterio | AnalisePorCriterio[] | null | undefined,
): string | null {
  const arr = listarAnalisesCriterio(analise);
  const done = arr.filter((a) => analiseCriterioComRespostaAposSolicitacao(a));
  done.sort((a, b) => tsAdequado(b) - tsAdequado(a));
  for (const x of done) {
    const o = x.observacaoSindicato;
    if (o != null && String(o).trim() !== "") return String(o);
  }
  return null;
}
